import { useEffect, useState } from "react";
import { format, isValid, parse } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

/**
 * The one date control on desktop (DESIGN.md §5.2). It replaces the browser's
 * `type="date"` / `type="datetime-local"` fields, which showed "dd-mm-yyyy --:-- --"
 * and looked different on every page and every browser.
 *
 * - Closed it reads like a chip: "1 Sep", or "1 Sep 2025" when the year is not this one.
 * - Typed entry: click into it and type DD-MM-YYYY (an ISO YYYY-MM-DD also parses).
 * - The calendar button opens a month calendar.
 * - `withTime` adds a separate 15-minute time select (only where time matters).
 *
 * The value in and out is the same string the native field used — "YYYY-MM-DD", or
 * "YYYY-MM-DDTHH:mm" with `withTime` — so callers and their APIs are unchanged.
 * An empty field is "".
 */

const ISO = "yyyy-MM-dd";
const TYPED_FORMATS = ["dd-MM-yyyy", "d-M-yyyy", "dd/MM/yyyy", "d/M/yyyy", ISO];

const parseDay = text => {
  const raw = (text || "").trim();
  if (!raw) return null;
  for (const pattern of TYPED_FORMATS) {
    const day = parse(raw, pattern, new Date());
    if (isValid(day) && day.getFullYear() > 1900) return day;
  }
  return null;
};

const chipLabel = day => format(day, day.getFullYear() === new Date().getFullYear() ? "d MMM" : "d MMM yyyy");

const TIMES = Array.from({ length: 96 }, (_, i) => `${String(Math.floor(i / 4)).padStart(2, "0")}:${String((i % 4) * 15).padStart(2, "0")}`);

export function DatePicker({
  value = "",
  onChange,
  withTime = false,
  defaultTime = "00:00",
  min,
  max,
  placeholder = "Pick a date",
  ariaLabel,
  testId,
  className,
  disabled = false,
}) {
  const [isoDay = "", time = ""] = (value || "").split("T");
  const day = isoDay ? parseDay(isoDay) : null;
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(day ? format(day, "dd-MM-yyyy") : "");
  }, [focused, isoDay]); // eslint-disable-line react-hooks/exhaustive-deps

  const emit = (nextDay, nextTime = time || defaultTime) => {
    if (!nextDay) return onChange?.("");
    const iso = format(nextDay, ISO);
    onChange?.(withTime ? `${iso}T${nextTime}` : iso);
  };

  const onType = event => {
    const text = event.target.value;
    setDraft(text);
    // A pasted or programmatic ISO datetime ("2026-08-26T00:00") carries its time.
    const [datePart, timePart] = text.split("T");
    const parsed = parseDay(datePart);
    if (parsed) emit(parsed, withTime && /^\d{2}:\d{2}/.test(timePart || "") ? timePart.slice(0, 5) : undefined);
    else if (!text.trim()) emit(null);
  };

  const minDay = min ? parseDay(min.split("T")[0]) : null;
  const maxDay = max ? parseDay(max.split("T")[0]) : null;
  const timeValue = TIMES.includes(time) ? time : (time ? time.slice(0, 5) : "");

  return (
    <span className={cn("inline-flex items-center gap-tight", className)}>
      <span className="relative inline-flex items-center">
        <input
          type="text"
          inputMode="numeric"
          className="h-8 w-[8.5rem] rounded-md border border-input bg-card pl-snug pr-8 text-body tabular-nums outline-none focus:border-primary"
          value={focused ? draft : day ? chipLabel(day) : ""}
          placeholder={focused ? "DD-MM-YYYY" : placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onChange={onType}
          aria-label={ariaLabel}
          aria-invalid={focused && draft.trim() && !parseDay(draft.split("T")[0]) ? "true" : undefined}
          disabled={disabled}
          data-testid={testId}
        />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="absolute right-1 inline-flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={ariaLabel ? `Open calendar for ${ariaLabel}` : "Open calendar"}
              disabled={disabled}
              data-testid={testId && `${testId}-calendar-button`}
            >
              <CalendarDays className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          {/* Portalled outside the page, so it carries the compact density itself:
              without it the global touch rule makes every day a 48px button. */}
          <PopoverContent className="w-auto max-w-[calc(100vw-16px)] p-0" align="start" collisionPadding={8} data-density="compact">
            <Calendar
              mode="single"
              selected={day || undefined}
              defaultMonth={day || maxDay || undefined}
              onSelect={next => { if (next) { emit(next); setOpen(false); } }}
              disabled={[minDay && { before: minDay }, maxDay && { after: maxDay }].filter(Boolean)}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </span>
      {withTime && (
        <Select value={timeValue || undefined} onValueChange={next => emit(day || new Date(), next)} disabled={disabled}>
          <SelectTrigger className="h-8 w-[5.5rem] text-body tabular-nums" aria-label={ariaLabel ? `${ariaLabel} time` : "Time"} data-testid={testId && `${testId}-time`}>
            <SelectValue placeholder="Time" />
          </SelectTrigger>
          <SelectContent data-density="compact" className="max-h-64">
            {(timeValue && !TIMES.includes(timeValue) ? [timeValue, ...TIMES] : TIMES).map(t => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </span>
  );
}

export default DatePicker;
